import { setDeviceUuid } from "@wkt/lib/auth-context";
import { setLastKnownPosition } from "@wkt/lib/device";

type CordovaBridgeEventType =
  | "raw-message"
  | "native-device"
  | "native-geolocation"
  | "native-geolocation-error"
  | "native-process-url"
  | "jira-oauth-callback"
  | "native-callback"
  | "native-log"
  | "notification-token"
  | "platform"
  | "device-uuid";

type CordovaBridgeListener<T = unknown> = (payload: T) => void;

const bridgeListeners = new Map<
  CordovaBridgeEventType,
  Set<CordovaBridgeListener>
>();

let bridgeInstalled = false;

function canUseWindow(): boolean {
  return typeof window !== "undefined";
}

function emitBridgeEvent<T = unknown>(
  type: CordovaBridgeEventType,
  payload: T,
): void {
  bridgeListeners.get(type)?.forEach((listener) => {
    try {
      listener(payload);
    } catch (error) {
      console.error(`Cordova bridge listener failed for ${type}`, error);
    }
  });
}

function updateLocationFromMessage(message: string): void {
  if (!message.startsWith("GEO_UPDATE:")) {
    return;
  }

  const rawCoordinates = message.substring("GEO_UPDATE:".length).split(",");
  const latitude = Number.parseFloat(rawCoordinates[0] ?? "");
  const longitude = Number.parseFloat(rawCoordinates[1] ?? "");

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return;
  }

  setLastKnownPosition(latitude, longitude);
}

function processIncomingMessage(message: string): void {
  emitBridgeEvent("raw-message", message);

  if (!canUseWindow()) {
    return;
  }

  if (message.startsWith("KC_SET_TOKEN:") || message.startsWith("KC_TOKEN:")) {
    const prefix = message.startsWith("KC_SET_TOKEN:")
      ? "KC_SET_TOKEN:"
      : "KC_TOKEN:";
    window.kc_token = message.substring(prefix.length);
    return;
  }

  if (
    message.startsWith("KC_SET_PROFILE:") ||
    message.startsWith("KC_PROFILE:")
  ) {
    const prefix = message.startsWith("KC_SET_PROFILE:")
      ? "KC_SET_PROFILE:"
      : "KC_PROFILE:";
    const rawProfile = message.substring(prefix.length);
    try {
      window.kc_profile = JSON.parse(rawProfile);
    } catch (error) {
      console.warn("Unable to parse Keycloak profile payload", error);
    }
    return;
  }

  if (message.startsWith("TOKEN:")) {
    const token = message.substring("TOKEN:".length) || "";
    window.deviceNotificationToken = token;
    emitBridgeEvent("notification-token", token);
    return;
  }

  if (message.startsWith("PLATFORM:")) {
    const platform = message.substring("PLATFORM:".length) || "browser";
    window.deviceNativePlatform = platform;
    emitBridgeEvent("platform", platform);
    return;
  }

  if (message.startsWith("DEVICE_UUID:")) {
    const deviceUuid = message.substring("DEVICE_UUID:".length) || "";
    window.deviceNativeUUID = deviceUuid;
    setDeviceUuid(deviceUuid);
    emitBridgeEvent("device-uuid", deviceUuid);
    return;
  }

  if (message.startsWith("PROCESS_URL:")) {
    emitBridgeEvent(
      "native-process-url",
      message.substring("PROCESS_URL:".length),
    );
    return;
  }

  if (message.startsWith("JIRA_OAUTH:")) {
    emitBridgeEvent(
      "jira-oauth-callback",
      message.substring("JIRA_OAUTH:".length),
    );
    return;
  }

  if (message.startsWith("NATIVE_CALLBACK:")) {
    emitBridgeEvent(
      "native-callback",
      message.substring("NATIVE_CALLBACK:".length),
    );
    return;
  }

  if (message.startsWith("LOG:")) {
    emitBridgeEvent("native-log", message.substring("LOG:".length));
    return;
  }

  if (message.startsWith("GEO_ERRO:") || message.startsWith("GEO_ERROR:")) {
    emitBridgeEvent("native-geolocation-error", message);
    return;
  }

  if (message.startsWith("GEO_UPDATE:")) {
    updateLocationFromMessage(message);
    emitBridgeEvent("native-geolocation", message);
    return;
  }

  if (message.startsWith("NE_") || message.startsWith("WEBRTC_")) {
    emitBridgeEvent("native-device", message);
  }
}

function handleWindowMessage(event: MessageEvent): void {
  if (typeof event.data !== "string" || event.data.length === 0) {
    return;
  }

  processIncomingMessage(event.data);
}

function hideSplashLoader(): void {
  if (!canUseWindow()) {
    return;
  }

  const splashLoader = document.getElementById("splash-loader");
  if (!splashLoader || splashLoader.dataset.hiding === "true") {
    return;
  }

  splashLoader.dataset.hiding = "true";
  splashLoader.classList.add("is-hiding");

  const removeSplash = () => {
    splashLoader.remove();
  };

  const onTransitionEnd = (event: TransitionEvent) => {
    if (event.target !== splashLoader || event.propertyName !== "opacity") {
      return;
    }
    splashLoader.removeEventListener("transitionend", onTransitionEnd);
    removeSplash();
  };

  splashLoader.addEventListener("transitionend", onTransitionEnd);
  // Fallback por si el transitionend no dispara (tab en background, etc.)
  window.setTimeout(removeSplash, 600);
}

function sendMessageToCordovaWrapper(message: string): void {
  if (!canUseWindow()) {
    return;
  }

  if (typeof window.sendCordovaWrapperMessage === "function") {
    window.sendCordovaWrapperMessage(message);
  } else if (window.parent && window.parent !== window) {
    window.parent.postMessage(message, "*");
  }

  if (message === "INIT_COMPLETED") {
    hideSplashLoader();
  }
}

export function installCordovaBridge(): void {
  if (!canUseWindow() || bridgeInstalled) {
    return;
  }

  window.addEventListener("message", handleWindowMessage);
  window.processCordovaMessage = (message) => {
    dispatchCordovaMessage(message);
  };
  bridgeInstalled = true;
}

export function dispatchCordovaMessage(message: string): void {
  processIncomingMessage(message);
}

export function subscribeCordovaBridgeEvent<T = string>(
  type: CordovaBridgeEventType,
  listener: CordovaBridgeListener<T>,
): () => void {
  const typedListener = listener as CordovaBridgeListener;
  const listeners = bridgeListeners.get(type) ?? new Set<CordovaBridgeListener>();
  listeners.add(typedListener);
  bridgeListeners.set(type, listeners);

  return () => {
    listeners.delete(typedListener);
    if (listeners.size === 0) {
      bridgeListeners.delete(type);
    }
  };
}

export function useCordovaBridge() {
  function sendMessage(message: string): void {
    sendMessageToCordovaWrapper(message);
  }

  return {
    install: installCordovaBridge,
    dispatchMessage: dispatchCordovaMessage,
    subscribe: subscribeCordovaBridgeEvent,
    sendMessage,
    hidePreloader: () => sendMessage("HIDE_PRELOADER"),
    showPreloader: () => sendMessage("SHOW_PRELOADER"),
    reloadApp: () => sendMessage("RELOAD_APPLICATION"),
    readyToProcessUrl: () => sendMessage("READY_TO_PROCESS_URL"),
    readyToProcessNotifications: () =>
      sendMessage("READY_TO_PROCESS_NOTIFICATIONS"),
    requestGeolocationUpdate: () => sendMessage("REQUEST_GEOLOCATION"),
    removeAllGeofences: () => sendMessage("GEOFENCE_REMOVEALL"),
    addOrUpdateGeofence: (payload: string) =>
      sendMessage(`GEOFENCE_ADDORUPDATE:${payload}`),
    openExternalUrl: (url: string) => sendMessage(`OPEN_URL:${url}`),
    setInitProgress: (step: string) =>
      sendMessage(`INITIALIZATION_PROGRESS:${step}`),
    setAppIconBadge: (count: number) => sendMessage(`UPDATE_BADGE:${count}`),
    emitNativeDeviceEvent: (message: string) => dispatchCordovaMessage(message),
  };
}
